/**
 * ADMIN FEATURES REACT QUERY HOOKS
 *
 * React Query hooks for managing feature flags in super admin dashboard
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FeatureFlagService,
  type UpdateFeatureRequest,
  type UpdatePricingRequest,
  type SetOverrideRequest,
} from '@/services/featureFlagService';
import { useToast } from '@/hooks/use-toast';

// =====================================================
// QUERY HOOKS
// =====================================================

/**
 * Get all feature definitions with pricing
 */
export function useAdminFeatures() {
  return useQuery({
    queryKey: ['admin-features'],
    queryFn: () => FeatureFlagService.getAllFeatures(),
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Get feature stats
 */
export function useAdminFeatureStats() {
  return useQuery({
    queryKey: ['admin-feature-stats'],
    queryFn: () => FeatureFlagService.getFeatureStats(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get all overrides for a specific company
 */
export function useCompanyOverrides(companyId: string | null) {
  return useQuery({
    queryKey: ['company-overrides', companyId],
    queryFn: () => FeatureFlagService.getCompanyOverrides(companyId!),
    enabled: !!companyId,
    staleTime: 1 * 60 * 1000,
  });
}

/**
 * Get all company overrides for a specific feature
 */
export function useFeatureOverrides(featureId: string | null) {
  return useQuery({
    queryKey: ['feature-overrides', featureId],
    queryFn: () => FeatureFlagService.getFeatureOverrides(featureId!),
    enabled: !!featureId,
    staleTime: 1 * 60 * 1000,
  });
}

// =====================================================
// MUTATION HOOKS
// =====================================================

/**
 * Update feature definition
 */
export function useUpdateFeature() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateFeatureRequest }) =>
      FeatureFlagService.updateFeature(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-features'] });
      queryClient.invalidateQueries({ queryKey: ['admin-feature-stats'] });
      toast({
        title: 'Feature Updated',
        description: 'Feature settings have been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Updating Feature',
        description: error.message,
      });
    },
  });
}

/**
 * Update feature pricing
 */
export function useUpdateFeaturePricing() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ featureId, pricing }: { featureId: string; pricing: UpdatePricingRequest }) =>
      FeatureFlagService.updateFeaturePricing(featureId, pricing),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-features'] });
      toast({
        title: 'Pricing Updated',
        description: 'Feature pricing has been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Updating Pricing',
        description: error.message,
      });
    },
  });
}

/**
 * Set company feature override
 */
export function useSetCompanyOverride() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (params: SetOverrideRequest) =>
      FeatureFlagService.setCompanyFeatureOverride(params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-features'] });
      queryClient.invalidateQueries({ queryKey: ['company-overrides', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['feature-overrides', variables.featureId] });
      queryClient.invalidateQueries({ queryKey: ['company-features'] });
      toast({
        title: 'Override Set',
        description: `Feature has been ${variables.enabled ? 'enabled' : 'disabled'} for this company.`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Setting Override',
        description: error.message,
      });
    },
  });
}

/**
 * Remove company feature override
 */
export function useRemoveCompanyOverride() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ companyId, featureId }: { companyId: string; featureId: string }) =>
      FeatureFlagService.removeCompanyFeatureOverride(companyId, featureId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-features'] });
      queryClient.invalidateQueries({ queryKey: ['company-overrides', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['feature-overrides', variables.featureId] });
      queryClient.invalidateQueries({ queryKey: ['company-features'] });
      toast({
        title: 'Override Removed',
        description: 'Feature will now follow plan defaults.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Removing Override',
        description: error.message,
      });
    },
  });
}
